import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/api';
import { MdOutlineBlock, MdDownload } from 'react-icons/md';
import Loader from '../Loader';
import { FaSearch } from "react-icons/fa";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const StkResultCdr = () => {

  const [stkresultcdr, setStkresultcdr] = useState([]);
  const [searchStkresultcdr, setSearchstkresultcdr] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searched, setSearched] = useState(false);

  // Convert a date to partition format pYYYYMMDD
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };


  // Fetch all stk result cdr
  const getStkresultcdr = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = `?tran_id=${searchStkresultcdr}`

      if (!searchStkresultcdr) {
        setLoading(false);
        return;
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 10) {
          alert('Date range cannot exceed 10 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);

        while (current <= end) {
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      const response = await axios.get(`/payments/stk_result_cdr${query}`);
      setStkresultcdr(response.data.items);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error.response) {
        setError('Network error! Check your connection.');
      } else if (error.response.status >= 500) {
        setError('Server error! Please try again later.');
      } else {
        setError('An unexpected error occurred!');
      }
    }
  }, [searchStkresultcdr, startDate, endDate]);

  // Handle search button click
  const handleSearch = () => {
    setSearched(true);
    getStkresultcdr();
  };

  // Handle clearing search inputs
  const handleClearSearch = () => {
    setSearchstkresultcdr('');
    setStartDate('')
    setEndDate('')
    setStkresultcdr([]); 
    setSearched(false); 
  };

  // Fetch data only if a search has been performed
  useEffect(() => {
    if (searched) getStkresultcdr();
  }, [getStkresultcdr, searched]);

  const downloadExcel = () => {
    if (!stkresultcdr.length) return alert('No data to download.');
  
    const worksheet = XLSX.utils.json_to_sheet(stkresultcdr);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sucessful request');
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, `stk-results-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className='mx-auto p-4'>
      <h3 className="my-2"><b>Stk Result</b></h3>
      <div className='bg-white rounded-lg p-4 xl:w-[78vw] 2xl:w-full'>
        <div className="flex flex-wrap items-center gap-2 py-2">
          <label htmlFor='search'><span className='hidden'>Search</span>
            <input
              type='search'
              id='search'
              className='px-3 py-1 w-full border bg-[#eef1ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              required
              placeholder='Search transaction...'
              value={searchStkresultcdr}
              onChange={(e) => setSearchstkresultcdr(e.target.value)}
            />
          </label>
          <label htmlFor="">
            <span className='hidden'>Start Date</span>
            <input 
              type="date"
              className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              value={startDate}
              onChange={(e) => {setStartDate(e.target.value)}}
            />
          </label>
          <label htmlFor="">
            <span className='hidden'>End Date</span>
            <input 
              type="date" 
              className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              value={endDate}
              onChange={(e) => {setEndDate(e.target.value)}}
            />
          </label>
          <button
            onClick={handleSearch}
            className='px-4 py-1 bg-[#2b1e3e] text-white rounded-md'
          >
            Search
          </button>
          <button
            onClick={handleClearSearch}
            className='px-4 py-1 bg-red-500 text-white rounded-md'
          >
            Clear
          </button>
          <button
            onClick={downloadExcel}
            className='flex items-center gap-1 bg-[#2b1e3e] text-white px-3 py-1 rounded-md hover:bg-[#3d2b56]'
          >
            <MdDownload />
            Export
          </button>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#eef1ff] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#eef1ff] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          searched ? (
            <div className='overflow-x-auto rounded-lg'>
              {stkresultcdr.length > 0
                ? (
                  <table className='w-full text-left table-auto'>
                    <thead>
                      <tr className='border-b border-slate-500'>
                        <th className='p-2'>Transaction Id</th>
                        <th className='p-2'>Phone Number</th>
                        <th className='p-2'>Amount</th>
                        <th className='p-2'>Status Desc</th>
                        <th className='p-2'>Result Desc</th>
                        <th className='p-2'>Checkout Request</th>
                        <th className='p-2'>Transaction Time</th>
                        <th className='p-2'>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stkresultcdr.map((c2bconfirm) => (
                        <tr key={c2bconfirm.id}>
                          <td className='p-2'>{c2bconfirm.tran_id}</td>
                          <td className='p-2'>{c2bconfirm.msisdn}</td>
                          <td className='p-2'>{c2bconfirm.amount}</td>
                          <td className='p-2'>{c2bconfirm.status_desc}</td>
                          <td className='p-2'>{c2bconfirm.result_desc}</td>
                          <td className='p-2'>{c2bconfirm.checkout_request_id}</td>
                          <td className='p-2'>{new Date(c2bconfirm.tran_time).toLocaleString("en-CA", { hour12: false })}</td>
                          <td className='p-2'>{new Date(c2bconfirm.created_at).toLocaleString("en-CA", { hour12: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className='bg-[#eef1ff] text-[#134463] grid place-items-center h-[70vh]'>
                    <div className='grid place-items-center p-4'>
                      <h3><MdOutlineBlock /></h3>
                      <h4>No Data</h4>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className='grid place-items-center h-[70vh]'>
              <div className='text-[#134463] p-4'>
                <h3 className='flex items-center justify-center'><FaSearch /></h3>
                <h4>Search STK Results</h4>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default StkResultCdr;
